import { useRef, useLayoutEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { FileText, Shield, Check, PenTool } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

const proposalFeatures = [
  'Scope + deliverables',
  'Milestones + timeline',
  'Assumptions + acceptance criteria',
];

const contractFeatures = [
  'MSA + SOW templates',
  'NDA + data processing addendum',
  'GST/VAT-ready invoices',
];

const milestones = [
  { name: 'Kickoff & discovery', week: 'Week 1' },
  { name: 'Architecture review', week: 'Week 2' },
  { name: 'Implementation plan', week: 'Week 3' },
  { name: 'Final delivery', week: 'Week 4' },
];

export default function ProposalSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const leftPanelRef = useRef<HTMLDivElement>(null);
  const rightPanelRef = useRef<HTMLDivElement>(null);
  const milestonesRef = useRef<(HTMLDivElement | null)[]>([]);
  const signatureRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      // Title animation
      gsap.fromTo(
        titleRef.current,
        { y: 24, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.6,
          scrollTrigger: {
            trigger: titleRef.current,
            start: 'top 80%',
            toggleActions: 'play none none reverse',
          },
        }
      );

      // Left panel
      gsap.fromTo(
        leftPanelRef.current,
        { x: '-6vw', opacity: 0 },
        {
          x: 0,
          opacity: 1,
          duration: 0.6,
          scrollTrigger: {
            trigger: leftPanelRef.current,
            start: 'top 80%',
            toggleActions: 'play none none reverse',
          },
        }
      );

      // Right panel
      gsap.fromTo(
        rightPanelRef.current,
        { x: '6vw', opacity: 0 },
        {
          x: 0,
          opacity: 1,
          duration: 0.6,
          delay: 0.1,
          scrollTrigger: {
            trigger: rightPanelRef.current,
            start: 'top 80%',
            toggleActions: 'play none none reverse',
          },
        }
      );

      // Milestones stagger
      milestonesRef.current.forEach((milestone, index) => {
        if (milestone) {
          gsap.fromTo(
            milestone,
            { x: -20, opacity: 0 },
            {
              x: 0,
              opacity: 1,
              duration: 0.4,
              delay: 0.2 + index * 0.1,
              scrollTrigger: {
                trigger: leftPanelRef.current,
                start: 'top 70%',
                toggleActions: 'play none none reverse',
              },
            }
          );
        }
      });

      // Signature line draw
      if (signatureRef.current) {
        gsap.fromTo(
          signatureRef.current,
          { scaleX: 0 },
          {
            scaleX: 1,
            duration: 0.8,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: signatureRef.current,
              start: 'top 80%',
              toggleActions: 'play none none reverse',
            },
          }
        );
      }
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="section-flowing bg-navy relative z-[60]"
    >
      <div className="w-full px-6 lg:px-12 max-w-7xl mx-auto">
        {/* Title */}
        <div ref={titleRef} className="text-center mb-16 opacity-0">
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-teal mb-4 block">
            Contracting
          </span>
          <h2 className="font-heading text-h2 sm:text-h1 font-bold text-white max-w-3xl mx-auto">
            Proposals, contracts, and payments—built in
          </h2>
        </div>

        {/* Two Panels */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Panel - Proposal */}
          <div
            ref={leftPanelRef}
            className="surface-panel p-8 opacity-0"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-teal/10 flex items-center justify-center">
                <FileText className="w-6 h-6 text-teal" />
              </div>
              <div>
                <h3 className="font-heading text-xl font-semibold text-white">
                  Structured proposals
                </h3>
              </div>
            </div>

            <ul className="space-y-3 mb-8">
              {proposalFeatures.map((feature) => (
                <li key={feature} className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-teal mt-0.5 flex-shrink-0" />
                  <span className="text-slate">{feature}</span>
                </li>
              ))}
            </ul>

            {/* Mock Milestones */}
            <div className="bg-white/5 rounded-xl p-4">
              <h4 className="font-mono text-xs uppercase tracking-wider text-slate mb-4">
                Sample milestones
              </h4>
              <div className="space-y-3">
                {milestones.map((milestone, index) => (
                  <div
                    key={milestone.name}
                    ref={(el) => { milestonesRef.current[index] = el; }}
                    className="flex items-center justify-between py-2 border-b border-white/5 last:border-0 opacity-0"
                  >
                    <span className="text-sm text-white">{milestone.name}</span>
                    <span className="text-xs font-mono text-slate">
                      {milestone.week}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Panel - Contract */}
          <div
            ref={rightPanelRef}
            className="surface-panel p-8 opacity-0"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-teal/10 flex items-center justify-center">
                <Shield className="w-6 h-6 text-teal" />
              </div>
              <div>
                <h3 className="font-heading text-xl font-semibold text-white">
                  E-sign + compliance
                </h3>
              </div>
            </div>

            <ul className="space-y-3 mb-8">
              {contractFeatures.map((feature) => (
                <li key={feature} className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-teal mt-0.5 flex-shrink-0" />
                  <span className="text-slate">{feature}</span>
                </li>
              ))}
            </ul>

            {/* Mock Signature */}
            <div className="bg-white/5 rounded-xl p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <p className="text-sm text-white mb-1">Statement of Work</p>
                  <p className="text-xs text-slate">SOW-2024-001</p>
                </div>
                <span className="px-2 py-1 text-xs font-medium bg-teal/20 text-teal rounded">
                  Ready to sign
                </span>
              </div>

              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <p className="text-xs text-slate mb-2">Client signature</p>
                  <div
                    ref={signatureRef}
                    className="h-12 border-b border-white/20 flex items-end origin-left"
                    style={{ transform: 'scaleX(0)' }}
                  >
                    <PenTool className="w-5 h-5 text-teal mb-2" />
                  </div>
                </div>
                <div className="flex-1">
                  <p className="text-xs text-slate mb-2">CXO signature</p>
                  <div className="h-12 border-b border-white/20 flex items-end">
                    <span className="text-teal font-script text-lg mb-1">
                      D. Kim
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
